import { assignJsonKey, cloneJson, isDangerousKey, isPlainObject } from './json'

/**
 * JSON Merge Patch (RFC 7396).
 * A `null` patch member deletes an existing key. `undefined` is ignored so a
 * missing view value cannot delete server data. Arrays are replaced wholesale.
 * The last appendix A row follows the published algorithm (and its errata):
 * null does not create a key that was not already there.
 */
export function mergePatch(target: unknown, patch: unknown): unknown {
  if (!isPlainObject(patch)) {
    return cloneJson(patch)
  }

  const base: Record<string, unknown> = {}
  if (isPlainObject(target)) {
    const cloned = cloneJson(target) as Record<string, unknown>
    for (const key of Object.keys(cloned)) {
      assignJsonKey(base, key, cloned[key])
    }
  }

  for (const key of Object.keys(patch)) {
    if (isDangerousKey(key)) {
      continue
    }

    const value = patch[key]
    if (value === undefined) {
      continue
    }

    if (value === null) {
      delete base[key]
      continue
    }

    assignJsonKey(base, key, mergePatch(base[key], value))
  }

  return base
}

/** Rebuild an array from its raw items and merge a patch into one index. */
export function arrayItemPatch(
  rawArray: readonly unknown[],
  index: number,
  itemPatch: unknown
): unknown[] {
  return rawArray.map((item, itemIndex) => {
    if (itemIndex !== index) {
      return cloneJson(item)
    }

    if (isPlainObject(item) && isPlainObject(itemPatch)) {
      return mergePatch(item, itemPatch)
    }

    return cloneJson(itemPatch)
  })
}
